import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { ApiError } from '../api/client';
import { isTwoStep, useLogin, useLoginCode, useMe } from '../auth/session';
import { credentialsFrom } from '../auth/credentials';
import { signInErrorText } from '../auth/signInError';
import { applyLanguage, LANGUAGES } from '../i18n';
import ui from '../styles/ui.module.css';
import styles from './LoginPage.module.css';
import { Brand } from '../components/Logo';
import { PasswordInput } from '../components/PasswordInput';


export function LoginPage() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const login = useLogin();
  const loginCode = useLoginCode();
  const navigate = useNavigate();
  const location = useLocation();
  // The fields keep their own contents (the browser may fill them in; see auth/credentials.ts).
  // The email is kept for when an expired code sends the administrator back to the password.
  const [email, setEmail] = useState('');
  // The administrator's second step: the 6-digit code from the authenticator app.
  const [codeStep, setCodeStep] = useState(false);
  const [code, setCode] = useState('');
  // The code field takes the focus when the step appears (autoFocus alone did not hold when the
  // password form was swapped for this one), so the code can be typed at once.
  const codeInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (codeStep) codeInput.current?.focus();
  }, [codeStep]);
  // Submitted with a field empty (nothing typed, nothing filled in by the browser).
  const [missing, setMissing] = useState(false);

  const from = (location.state as { from?: string } | null)?.from ?? '/';
  if (me.data) return <Navigate to={me.data.admin ? '/admin/users' : from} replace />;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    loginCode.reset();
    const credentials = credentialsFrom(event.currentTarget as HTMLFormElement);
    setMissing(!credentials);
    if (!credentials) return;
    setEmail(credentials.email);
    login.mutate(
      credentials,
      {
        onSuccess: (result) => {
          if (isTwoStep(result)) {
            setCodeStep(true);
          } else {
            navigate(from, { replace: true });
          }
        },
      },
    );
  }

  function onCode(event: FormEvent) {
    event.preventDefault();
    loginCode.mutate(code, {
      onSuccess: () => navigate('/admin/users', { replace: true }),
      onError: (error) => {
        // Too late or too many tries: back to the password.
        if (error instanceof ApiError && error.code === 'codeExpired') {
          setCodeStep(false);
          setCode('');
        }
      },
    });
  }

  // Back at the password after an expired code, that is the message to show.
  const error = codeStep
    ? signInErrorText(loginCode.error, t)
    : missing
      ? t('login.missing')
      : signInErrorText(login.error ?? loginCode.error, t);

  return (
    <div className={styles.wrap}>
      {codeStep ? (
        <form className={`${ui.card} ${styles.card}`} onSubmit={onCode} noValidate>
          <Brand size={52} className={styles.brand} />
          <h1 className={styles.title}>{t('login.codeTitle')}</h1>
          <p className={ui.muted}>{t('login.codeHint')}</p>
          {error && (
            <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
              {error}
            </div>
          )}
          <label className={ui.field}>
            <span className={ui.label}>{t('login.code')}</span>
            <input
              className={`${ui.input} ${styles.code}`}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              ref={codeInput}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </label>
          <button type="submit" className={`${ui.button} ${ui.primary} ${styles.submit}`} disabled={code.length !== 6 || loginCode.isPending}>
            {t('login.verify')}
          </button>
        </form>
      ) : (
        <form className={`${ui.card} ${styles.card}`} onSubmit={onSubmit} noValidate>
          <Brand size={52} className={styles.brand} />
          <p className={ui.muted}>{t('app.tagline')}</p>
          <h1 className={styles.title}>{t('login.title')}</h1>
          {error && (
            <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
              {error}
            </div>
          )}
          <label className={ui.field}>
            <span className={ui.label}>{t('login.email')}</span>
            <input
              className={ui.input}
              type="email"
              name="email"
              autoComplete="username"
              required
              defaultValue={email}
            />
          </label>
          <div className={ui.field}>
            <label htmlFor="login-password" className={ui.label}>
              {t('login.password')}
            </label>
            <PasswordInput
              id="login-password"
              name="password"
              autoComplete="current-password"
              required
            />
          </div>
          <button
            type="submit"
            className={`${ui.button} ${ui.primary} ${styles.submit}`}
            disabled={login.isPending}
          >
            {login.isPending ? t('login.submitting') : t('login.submit')}
          </button>
          <div className={styles.languages} role="group" aria-label={t('profile.language')}>
            {LANGUAGES.map((language) => (
              <button
                key={language}
                type="button"
                lang={language}
                aria-pressed={i18n.language === language}
                className={styles.languageButton}
                onClick={() => applyLanguage(language)}
              >
                {t(`languages.${language}`)}
              </button>
            ))}
          </div>
        </form>
      )}
    </div>
  );
}
